<script setup lang="ts">
import { computed } from "vue";
import type { ButtonProps } from "@nuxt/ui";

/**
 * UiButton - Thin wrapper around Nuxt UI Button component
 *
 * NOTE: Button variant styling (solid, outline, ghost, soft, link, subtle)
 * and color customization are globally defined in src/app.config.ts
 * under ui.button.color.primary
 *
 * This component only handles:
 * - Button group shape behavior (rounded edges for grouped buttons)
 * - Full rounded appearance with equal padding when rounded={true}
 * - Forwarding all other props to the underlying UButton component
 */

export interface Props extends /* @vue-ignore */ ButtonProps {
  rounded?: "left" | "right" | false | true;
  isGroup?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  type: "button",
  rounded: undefined,
});

const buttonProps = computed<ButtonProps>(() => {
  const { rounded: _rounded, isGroup: _isGroup, ...rest } = props;

  return rest;
});

const roundedClasses = computed(() => {
  // Full rounded when rounded={true}
  if (props.rounded === true) {
    return "rounded-full";
  }

  // Group shape classes only apply when in a button group
  if (!props.isGroup) {
    return "";
  }

  if (props.rounded === "left") {
    return "rounded-r-none";
  }

  if (props.rounded === "right") {
    return "rounded-l-none";
  }

  if (props.rounded === false) {
    return "rounded-none";
  }

  return "";
});

const circularPadding = computed(() => {
  if (props.rounded !== true) {
    return "";
  }

  // Map size to equal padding for circular buttons (overrides rectangular padding from app.config)
  const paddingMap: Record<string, string> = {
    xs: "!p-1.5",
    sm: "!p-2.5",
    md: "!p-3",
    lg: "!p-4",
    xl: "!p-5",
  };

  const size = props.size || "md";
  return paddingMap[size] || paddingMap.md;
});
</script>
<template>
  <UButton
    v-bind="buttonProps"
    :class="[roundedClasses, circularPadding]"
    :square="rounded === true"
    :type="props.type"
  >
    <slot />
  </UButton>
</template>
